import React, { useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useHistory } from 'react-router';
import classNames from 'classnames';

import lang from '../../../lib/language';
import { Payment } from '../../../lib/models/crm/types';
import { FxOrder } from '../../../lib/models/reporting/types';
import { RootState } from '../../../lib/store';
import { usePaymentFxOrders } from '../../../lib/util/hooks/wallet/PaymentAndFxHook';
import { TextHelpers } from '../../../lib/util/TextHelpers';

import CapitalEmptyImage from '../../../assets/img/image_empty.svg';
import { BaseModal, Button } from '../../../components';
import { ButtonTypesEnum } from '../../../components/Button/Button.types';
import ClickableDiv from '../../../components/ClickableDiv/ClickableDiv';
import DateRangePickerWrapper from '../../../components/DateRangePickerWrapper/DateRangePickerWrapper';
import { useIsMobile } from '../../../hooks';
import { UI_ROUTES } from '../../../util/routes';
import { XL_BREAKPOINT_WIDTH } from '../constants';
import Modal from '../modal/paymentsModal/BasicModal';
import FxOrderModal from '../modal/paymentsModal/FxOrderModal';

import FxOrderItem from './FXOrderItem';
import TransactionItem from './TransactionItem';

import styles from './Transactions.module.scss';

type Props = {
  isUserVerified: boolean;
  browserWidthSize: number;
} & ReduxProps;

const Transactions = (props: Props) => {
  const {
    allTransactions,
    isUserVerified,
    browserWidthSize,
  } = props;
  const isMobile = useIsMobile();

  const [showModal, setShowModal] = useState<boolean>(false);
  const [selectedFxOrder, setSelectedFxOrder] = useState<FxOrder|undefined>(undefined);
  const [infoModal, setInfoModal] = useState({ values: {} });

  const { hasMorePages, number: pageNumber, setDateRange, setPage, filteredContent } = usePaymentFxOrders();

  const maxRusultMob = 10;
  const history = useHistory();

  const handleDateChange = (value) => {
    setDateRange(value);
  };

  const handleModalTransfer = () => {
    setShowModal(!showModal);
  };

  const handlePaymentClick = (payment: Payment) => {
    const values = {
      hasLinkedAccounts: true,
      amount: payment.amount,
      create_date: payment.create_date,
      payment_method: payment.payment_method,
      category: TextHelpers.getPaymentProcessName(payment.payment_process),
      adjustmentCategory: payment.category,
      bankName: payment.sender_bank_name,
      paymentReference: payment.payment_reference,
      paymentStatus: payment.payment_status,
      paymentProcess: payment.payment_process,
      description: payment.description,
      payment,
    };

    setInfoModal({ ...infoModal, values });
    handleModalTransfer();
  };

  const handleButtonLoadMore = () => {
    setPage(pageNumber + 1);
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.wrapperTitle}>
        {isMobile && (
          <div id="transactionsTitle" className={styles.title}>{lang.mobileFundsScreenRecentTransactions()}</div>
        )}
        {filteredContent.length > maxRusultMob && isMobile && (
        <Button
          className={styles.viewAll}
          variant={ButtonTypesEnum.Link}
          onClick={() => history.push(UI_ROUTES.transactionHistory)}
        >
          {lang.commonViewAll()}
        </Button>
        )}
      </div>
      {!isMobile && (
      <div className={styles.titleViewWrapper}>
        <h6 id="transactionsTitle" className={styles.title}>{lang.webFundsTransactionsTitle()}</h6>
      </div>
      )}
      <div className={styles.wrapperDateRangePicker}>
        { allTransactions.length > 0 && (
        <>
          { !isMobile && (
            <DateRangePickerWrapper
              initialState={{
                startDate: new Date(allTransactions[allTransactions.length - 1].create_date),
                endDate: new Date(),
              }}
              maxDateCalendar={new Date()}
              minDateCalendar={new Date(allTransactions[allTransactions.length - 1].create_date)}
              onDateSelect={handleDateChange}
            />
          )}
        </>
        )}
      </div>
      {
        filteredContent.length === 0
        && (
        <div className={styles.wrapperNoTransactions}>
          <img className={styles.noMessagesLogo} src={CapitalEmptyImage} alt="no messages logo" />
          <p className={styles.noMessageText}>{lang.commonInboxNoMessagesText()}</p>
        </div>
        )
      }
      {
        (isUserVerified && allTransactions.length > 0) && (
        // eslint-disable-next-line no-nested-ternary
        <div className={isMobile
          ? styles.wrapperTransactionsMobile
          : (
            browserWidthSize >= XL_BREAKPOINT_WIDTH
              ? styles.wrapperTableFullScreen
              : styles.wrapperTableNoFullScreen
          )}
        >
          { isMobile ? (
            <div className={styles.itemWrapper}>
              {filteredContent.map((item, index) => {
                if (item.isPayment) {
                  const payment:Payment = item.orderOrPayment as Payment;
                  return (
                    <ClickableDiv key={payment.payment_reference} onClick={() => handlePaymentClick(payment)}>
                      <TransactionItem payment={payment} />
                    </ClickableDiv>
                  );
                }
                const order:FxOrder = item.orderOrPayment as FxOrder;
                return (
                  <ClickableDiv id={`transactionsItem_${index}`} key={order.orderId} onClick={() => setSelectedFxOrder(order)}>
                    <FxOrderItem fxOrder={order} />
                  </ClickableDiv>
                );
              })}
            </div>
          ) : (
            <>
              {filteredContent.map((item, index) => {
                if (item.isPayment) {
                  const payment:Payment = item.orderOrPayment as Payment;
                  return (
                    <ClickableDiv
                      className={styles.transactionRow}
                      key={payment.payment_reference}
                      onClick={() => handlePaymentClick(payment)}
                    >
                      <TransactionItem payment={payment} />
                    </ClickableDiv>
                  );
                }
                const order:FxOrder = item.orderOrPayment as FxOrder;
                return (
                  <ClickableDiv id={`transactionsItem_${index}`} className={styles.transactionRow} key={order.orderId} onClick={() => setSelectedFxOrder(order)}>
                    <FxOrderItem fxOrder={order} />
                  </ClickableDiv>
                );
              })}
            </>
          )}
          {
            hasMorePages && !isMobile && (
              <Button
                className={classNames(styles.btnLoadMore, { [styles.hideBtnLoadMore]: !hasMorePages })}
                variant={ButtonTypesEnum.Link}
                onClick={handleButtonLoadMore}
              >
                {lang.commonLoadMore()}
              </Button>
            )
          }
          { isMobile ? (
            <>
              {showModal && <Modal handleModal={handleModalTransfer} infoModal={infoModal} />}
              {!!selectedFxOrder && (
              <FxOrderModal
                handleModal={() => setSelectedFxOrder(undefined)}
                fxOrder={selectedFxOrder}
              />
              )}
            </>
          ) : (
            <BaseModal isOpen={showModal || !!selectedFxOrder}>
              {showModal && <Modal handleModal={handleModalTransfer} infoModal={infoModal} />}
              {!!selectedFxOrder && (
              <FxOrderModal
                handleModal={() => setSelectedFxOrder(undefined)}
                fxOrder={selectedFxOrder}
              />
              )}
            </BaseModal>
          )}
        </div>
        )
      }
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  allTransactions: state.payment.paymentsAll.data,
});

const connector = connect(mapStateToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(Transactions);
